import React, { useState } from 'react';
import {
  loadSLAsByWorkflows,
  exportSLAsByWorkflows,
} from '../../../actions/reportActions';
import { Table, SimpleTextFilter, TableColumn, getVisibleColumns } from '../../common/Table';
import { CommaNumber_NoDecimal } from '../../../util/formatter';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import WorkflowLink from '../../common/links/WorkflowLink';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

export interface SLAsByWorkflowsListProps {
  currentUser?: any;
}

function SLAsByWorkflowsList({ currentUser }: SLAsByWorkflowsListProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const [reload] = useState(undefined);
  const allColumns = [
    'name',
    'openRequests',
    'inSLA',
    'outsideSLA',
    'completeRequests',
    'averageDuration',
  ];
  const defaultVisibleColumns = allColumns;
  const [items, setItems] = useState<any>();
  const params = new URLSearchParams(location.search);
  const { tableSettings, handleSetTableSettings: setTableSettings, filters, filterChange, isLoading } =
    useTableSettings(
      'slabyworkflowreport-settings',
      getTableSettingsDefaults({ defaultSort: 'name', defaultVisibleColumns }),
      null,
      loadSLAsByWorkflows,
      setItems,
      reload,
      t('Failed to load report')
    );

  let columnsConfig: TableColumn[] = [
    {
      Key: 'name',
      Label: t('Workflow'),
      Render: (item: any) => (
        <WorkflowLink
          id={item.keyWorkflowId}
          name={item.name}
          colour={item.colourHex}
          currentUser={currentUser}
        />
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'openRequests',
      Label: t('Open requests'),
      className: 'text-right num no-wrap',
      Render: (row: any) => CommaNumber_NoDecimal(row.openRequests),
    },
    {
      Key: 'inSLA',
      Label: t('Within target'),
      className: 'text-right num no-wrap green',
      Render: (row: any) => CommaNumber_NoDecimal(row.inSLA),
    },
    {
      Key: 'outsideSLA',
      Label: t('Outside target'),
      className: 'text-right num no-wrap red',
      Render: (row: any) => CommaNumber_NoDecimal(row.outsideSLA),
    },
    {
      Key: 'completeRequests',
      Label: t('Completed requests'),
      className: 'text-right num no-wrap',
      Render: (row: any) => CommaNumber_NoDecimal(row.completeRequests),
    },
    {
      Key: 'averageDuration',
      Label: t('Average duration'),
      className: 'text-right no-wrap',
      Render: (row: any) => CommaNumber_NoDecimal(row.averageDuration),
    },
  ];

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return items ? (
    <KwPanel className="mb-4">
      <Table
        className="table-v-stripes"
        columns={columns}
        allColumns={allColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        rows={items.data.map((x: any) => ({ Key: x.keyWorkflowId, ...x }))}
        totalRowCount={items.totalRowCount}
        filteredRowCount={items.filteredRowCount}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        exportData={exportSLAsByWorkflows}
      />
    </KwPanel>
  ) : (
    <Loading></Loading>
  );
}

export default SLAsByWorkflowsList;
